"use client";

// While offline, the site and report ids in the audit URL are temporary
// placeholders (`offline-site-...` / `offline-report-...`) generated on the
// client. Once `syncManager` replays the queued create requests, redux swaps
// those placeholders for the real, server-assigned ids (matched by
// `_clientId`) - but the URL itself keeps whatever id was there when the
// page was opened. Any component that reads `siteId`/`reportId` straight
// from `useParams()` after that swap (e.g. by going back to the dashboard
// and returning to the audit sections while sync just finished) ends up
// issuing GET requests with the now-stale offline id.
//
// This hook resolves the current route's siteId/reportId against redux
// state: if either is still an offline placeholder but a synced record with
// a matching `_clientId` now exists, it returns the real id immediately (so
// the very next render already fetches with the right id) and replaces the
// URL in place so a refresh keeps working too.

import { useCallback, useEffect, useMemo } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import { isOfflineSiteId } from "../redux/slices/siteDataSlice.js";
import { isOfflineReportId } from "../redux/slices/auditReportSlice.js";

const EMPTY_ARRAY = [];
const EMPTY_REPORTS = {};
const EMPTY_IDS = {};

export function useResolvedAuditRouteIds() {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { projectId, section } = params;
  const rawSiteId = params.siteId;
  const rawReportId = params.reportId;

  const siteList =
    useSelector((state: any) => state.siteData?.siteData) || EMPTY_ARRAY;
  const currentSiteData = useSelector(
    (state: any) => state.siteData?.currentSiteData,
  );
  const allReportsData =
    useSelector((state: any) => state.auditReports?.allReportsData) ||
    EMPTY_REPORTS;
  const currentReport = useSelector(
    (state: any) => state.auditReports?.currentReport,
  );
  const recentReports =
    useSelector((state: any) => state.auditReports?.recentReports) ||
    EMPTY_ARRAY;

  const resolvedIds = useSelector((state: any) => state.offline?.resolvedIds) || EMPTY_IDS;

  const resolveId = useCallback((id: string | undefined) => {
    const seen = new Set<string>();
    while (id && resolvedIds[id] && !seen.has(id)) { seen.add(id); id = resolvedIds[id]; }
    return id;
  }, [resolvedIds]);

  const resolvedSiteId = useMemo(() => {
    const mapped = resolveId(rawSiteId);
    if (mapped !== rawSiteId) return mapped;
    if (!rawSiteId || !isOfflineSiteId(rawSiteId)) return rawSiteId;
    const clientId = rawSiteId.slice("offline-site-".length);
    const match =
      (currentSiteData?._clientId === clientId &&
      currentSiteData?._id &&
      !isOfflineSiteId(currentSiteData._id)
        ? currentSiteData
        : null) ||
      siteList.find(
        (s: any) =>
          s?._clientId === clientId && s?._id && !isOfflineSiteId(s._id),
      );
    return match?._id || rawSiteId;
  }, [rawSiteId, siteList, currentSiteData, resolveId]);

  const resolvedReportId = useMemo(() => {
    const mapped = resolveId(rawReportId);
    if (mapped !== rawReportId) return mapped;
    if (!rawReportId || !isOfflineReportId(rawReportId)) return rawReportId;
    const clientId = rawReportId.slice("offline-report-".length);

    if (
      currentReport?._clientId === clientId &&
      currentReport?._id &&
      !isOfflineReportId(currentReport._id)
    ) {
      return currentReport._id;
    }

    const fromAllReports = Object.values(allReportsData || {}).find(
      (entry: any) =>
        entry?.report?._clientId === clientId &&
        entry?.report?._id &&
        !isOfflineReportId(entry.report._id),
    ) as any;
    if (fromAllReports?.report?._id) return fromAllReports.report._id;

    const fromRecent = recentReports.find(
      (item: any) =>
        item?._clientId === clientId &&
        item?._id &&
        !isOfflineReportId(item._id),
    );
    return fromRecent?._id || rawReportId;
  }, [rawReportId, allReportsData, currentReport, recentReports, resolveId]);

  const idsChanged =
    resolvedSiteId !== rawSiteId || resolvedReportId !== rawReportId;

  useEffect(() => {
    if (!idsChanged) return;
    if (!resolvedSiteId || !resolvedReportId) return;
    if (isOfflineSiteId(resolvedSiteId) || isOfflineReportId(resolvedReportId))
      return;

    const search = location.search || "";
    const hash = location.hash || "";
    const nextPath = section
      ? `/audit/${resolvedSiteId}/${resolvedReportId}/${section}${search}${hash}`
      : `/audit/${resolvedSiteId}/${resolvedReportId}${search}${hash}`;
    navigate(nextPath, { replace: true });
  }, [
    idsChanged,
    resolvedSiteId,
    resolvedReportId,
    section,
    navigate,
    location.search,
    location.hash,
  ]);

  return {
    projectId,
    section,
    siteId: resolvedSiteId,
    reportId: resolvedReportId,
  };
}
